import { useCallback, useState } from 'react';

export interface UseMessageHoverReturn {
  /** ID of the currently hovered message, or null if none */
  hoveredMessageId: string | null;
  /** Handler to call when mouse enters a message */
  handleMouseEnter: (messageId: string) => void;
  /** Handler to call when mouse leaves a message */
  handleMouseLeave: () => void;
  /** Check if a specific message is hovered */
  isHovered: (messageId: string) => boolean;
}

/**
 * Hook to track which message is currently being hovered
 * Useful for showing timestamps, action buttons, or other contextual UI on hover
 *
 * @returns Hover state and event handlers
 *
 * @example
 * ```tsx
 * const { hoveredMessageId, handleMouseEnter, handleMouseLeave, isHovered } = useMessageHover();
 *
 * return messages.map(message => (
 *   <div
 *     key={message.id}
 *     onMouseEnter={() => handleMouseEnter(message.id)}
 *     onMouseLeave={handleMouseLeave}
 *   >
 *     <MessageContent {...message} />
 *     {isHovered(message.id) && <MessageActions />}
 *   </div>
 * ));
 * ```
 */
export const useMessageHover = (): UseMessageHoverReturn => {
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);

  const handleMouseEnter = useCallback((messageId: string) => {
    setHoveredMessageId(messageId);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredMessageId(null);
  }, []);

  const isHovered = useCallback(
    (messageId: string) => {
      return hoveredMessageId === messageId;
    },
    [hoveredMessageId]
  );

  return {
    hoveredMessageId,
    handleMouseEnter,
    handleMouseLeave,
    isHovered,
  };
};
